/**
 * useConfigurator — the public bundle's state machine, lifted out of the
 * legacy `App()` body. Returns the full state + actions tuple so the App
 * shell can stay route-only (under the 200-line target).
 *
 * Behavior preserved verbatim from App.tsx lines 1671-2287:
 *  - state shape (view, selectedCategory, cart, modal, etc.)
 *  - effects (cart sync, abandonment guard, summary scroll/focus)
 *  - actions (next/prev, toggleOption, handleSubmit, handleLogoUpload, etc.)
 *
 * Single export so the orchestrator sees `const cfg = useConfigurator(...)`
 * and forwards `cfg.foo` into the presentation components.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Cart,
  CustomerInfo,
  FlowConfig,
  LogoSelection,
  Selections,
  Step,
} from '../types';
import { computeUnitBoxPrice } from '../lib/pricing';
import { stepHasCategoryTabs } from '../lib/flowSteps';
import {
  buildPositionalSlots,
  buildSubmissionSteps,
  deriveCartBox,
  getMaxSelections,
  getOptionSteps,
  isCheckoutReady,
  isOtherOption,
  isUnlimitedStep,
  type OtherValues,
} from '../lib/orderPipeline';
import {
  LEGACY_EMAIL_TEMPLATE,
  buildEmailContext,
  buildSubmission,
  renderEmail,
} from '../lib/emailTemplate';
import {
  DEFAULT_FLOW_HERO_IMAGE,
  buildCategoryImageMap,
  resolveBoxDisplayName,
} from '../lib/categories';
import type {
  CategoryItem,
  DefaultsSurface,
  EmailTemplateSurface,
} from '../lib/content';
import { checkLogoSelection, MAX_LOGO_BYTES } from '@brandbox/content-schema';
import { resolveSendUrl } from '../lib/sendApi';
import { uploadLogos, LogoUploadError, type LogoBlobPointer } from '../lib/logoUpload';

export type View = 'landing' | 'configurator' | 'checkout';
// N6c — Storage / Shipment are now `string`: the option lists are store-ruled
// via the `checkout-labels` surface, so they're no longer a fixed literal
// union. The DEFAULT value stays 'DECIDE LATER' (set below + in clearCart), and
// the submitted string is unchanged, so the submission/email round-trip stays
// byte-identical.
export type Storage = string;
export type Shipment = string;
export type ModalKind =
  | 'clearConfirm'
  | 'abandon'
  | 'maxMerch'
  | 'maxFinalTouch'
  | 'configError'
  | 'quantityPrompt'
  | null;

export interface UseConfiguratorArgs {
  flows: Record<string, FlowConfig>;
  categories: CategoryItem[];
  defaults: DefaultsSurface;
  emailTemplate: EmailTemplateSurface | undefined;
  /**
   * Phase N1a: imperative loader for a flow not yet in `flows`. Called when
   * the operator/customer clicks a tile whose box hasn't been eagerly
   * loaded (anything beyond the static landing categories). Returns the
   * flow or `null` (deleted/404) — the hook fails gracefully, never falls
   * back to Spring.
   */
  loadFlow?: (id: string) => Promise<FlowConfig | null>;
  /**
   * Phase K+4: when the bundle is rendered inside the admin's preview
   * iframe, suppress real network calls (no /api/send POST). The hook
   * surfaces a friendly "submission disabled" error in this case.
   */
  isPreview?: boolean;
  /**
   * Phase N2b: the draft flow id from `?preview=<flowId>` (the URL the
   * admin's "Preview on site" button opens). When set and the flow has
   * landed in `flows` (via the credentialed preview fetch), the configurator
   * auto-opens that box so the operator immediately sees the unreleased
   * box's real configurator rather than the landing page.
   */
  previewFlowId?: string | null;
}

/**
 * Phase N1a — not-found sentinel. When a clicked box has no flow (the id is
 * unknown to both the bundle and the API), the hook holds this empty flow
 * instead of crashing on `flow.steps[i]` or silently rendering Spring. It
 * carries zero steps so the configurator renders nothing destructive; the
 * shell keeps the customer on a safe state. Distinct title so a Spring
 * fallback can never be mistaken for it.
 */
const NOT_FOUND_FLOW: FlowConfig = {
  flowId: '__not_found__',
  title: '',
  intro: '',
  steps: [{ id: '__empty__', title: '', type: 'summary' }],
};

/**
 * Redesign pass B (item 14) — client-side reference number fallback.
 *
 * PLACEHOLDER: the /api/send pipeline does not return a customer-facing
 * reference yet. When the backend starts returning one (`reference` /
 * `submissionId` / `id` on the success body), that value wins in
 * `handleSubmit` and this generator can be deleted. Unambiguous alphabet
 * (no 0/O/1/I) so the reference is easy to read back over the phone.
 */
function generateClientReference(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 6; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return `BB-${out}`;
}

/**
 * Phase N1a — generic "smart default" selections for a freshly-opened flow.
 * Pre-selects the first option of the first step IFF that step is a
 * single-select options step (`maxSelections === 1`). This reproduces
 * Spring/Office's historical `{ packaging: ['box'] }` byte-identically
 * (their first step is `packaging`, single-select, first option `box`)
 * while being driven entirely by the flow's own data — no id literals.
 *
 * Boxes whose first step is multi-select / upload / form / summary start
 * with an empty selection, matching today's "everything else → `{}`".
 */
function smartDefaultSelections(flow: FlowConfig): Selections {
  const first = flow.steps[0];
  if (!first) return {};
  const isOptionsStep = !first.type || first.type === 'options';
  if (isOptionsStep && first.maxSelections === 1 && first.options && first.options.length > 0) {
    return { [first.id]: [first.options[0].id] };
  }
  return {};
}

/**
 * N6c — resolve the box's preview/header image WITHOUT guessing an id-templated
 * path. The authoritative source is the admin-curated `categories` surface
 * (`categoryImageMap[id]`). When the admin hasn't curated an image for this
 * box id (a novel admin box not in the static category list), fall back to the
 * box's OWN `flow.heroImage`, then to the shared admin-driven
 * `DEFAULT_FLOW_HERO_IMAGE` — never the old `/images/general/${id}_box.webp`
 * literal (which 404s for any box whose image isn't named that way). This is
 * the same chain CheckoutPage already uses for its box-preview image. For
 * Spring/Office `categoryImageMap[id]` always resolves, so this is
 * byte-identical to the previous render.
 */
function resolveBoxImage(
  categoryImageMap: Record<string, string>,
  id: string,
  flow: FlowConfig | undefined,
): string {
  const curated = categoryImageMap[id];
  if (curated) return curated;
  const hero = (flow as { heroImage?: unknown } | undefined)?.heroImage;
  if (typeof hero === 'string' && hero.length > 0) return hero;
  return DEFAULT_FLOW_HERO_IMAGE;
}

export function useConfigurator(args: UseConfiguratorArgs) {
  const { flows, categories, defaults, emailTemplate, loadFlow, isPreview, previewFlowId } = args;
  const categoryImageMap = useMemo(() => buildCategoryImageMap(categories), [categories]);

  // Initial flow: the first static landing category's flow (spring at
  // launch) so the configurator's `flow.steps[0]` is always defined. The
  // landing view is what actually renders first, so this is just a safe
  // seed — the real flow is chosen the moment a tile is clicked.
  const initialFlowId = categories[0]?.id;
  const [flow, setFlow] = useState<FlowConfig>(
    // N6c — the `|| flows.spring` middle literal was dropped: seed from the
    // FIRST available landing category's flow, else the not-found sentinel.
    // The landing view renders first, so this is just a safe seed — the real
    // flow is chosen the moment a tile is clicked. No spring id literal.
    (initialFlowId && flows[initialFlowId]) || NOT_FOUND_FLOW,
  );
  const [view, setView] = useState<View>('landing');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedBox, setSelectedBox] = useState<{ name: string; image: string } | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selections, setSelections] = useState<Selections>({});
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({});
  // Chapter 10 (F15) — a company can have more than one logo, so the picked
  // artwork is a LIST. Order is the order the customer picked them in: the
  // first is the primary mark, and it is the one mirrored onto the legacy
  // single-logo submission fields.
  const [logoState, setLogoState] = useState<LogoSelection[]>([]);
  const logos = logoState;
  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // M4 (customer-logo pipeline) — recoverable logo-upload UX. The logo is
  // OPTIONAL: a failed upload must NEVER block the order. We use a two-step
  // model rather than silently dropping the logo:
  //   1st submit + upload fails → set this flag + a friendly `error`, and
  //   STOP (no order POST). The customer can fix the file and retry, OR click
  //   submit again to proceed WITHOUT the logo.
  //   2nd submit while the flag is set → skip the upload, submit the order
  //   without any blob pointer (the filenames + sizes still flow as today).
  //   Changing the selection clears the flag, so the next submit retries.
  //
  // F15 — the upload is all-or-nothing (`uploadLogos` posts the whole set in
  // one request and the endpoint refuses or accepts it whole), so this stays a
  // two-state model with several files rather than gaining a "some attached"
  // third state that the copy would have to explain.
  const [logoUploadFailed, setLogoUploadFailed] = useState(false);
  // Wrap the setter so changing the selection (a new logo, one removed, all
  // cleared) resets the recoverable-failure flag — otherwise a customer who
  // fixes the offending file would silently send none.
  const setLogos = useCallback((next: LogoSelection[]) => {
    setLogoState(next);
    setLogoUploadFailed(false);
  }, []);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Redesign pass B (item 14) — the reference number shown on the success
  // screen. Prefers an id returned by the /api/send pipeline; falls back to
  // a client-generated BB-XXXXXX.
  const [submissionRef, setSubmissionRef] = useState<string | null>(null);
  const [hasSetQuantity, setHasSetQuantity] = useState(false);
  // Phase N2b — generic per-step "Other…" free-text values, keyed by step id.
  // Replaces the three fixed slots (packaging/merch/finalTouchOther) so a box
  // can carry an Other slot on ANY number of steps. The legacy three accessors
  // below are derived from option-step positions 0/1/2 so ConfiguratorView /
  // CheckoutPage keep their existing prop contracts byte-identically for
  // Spring/Office.
  const [otherValues, setOtherValues] = useState<OtherValues>({});
  const setStepOther = useCallback((stepId: string, value: string) => {
    setOtherValues((prev) => ({ ...prev, [stepId]: value }));
  }, []);
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);
  const [abandonEmail, setAbandonEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [modal, setModal] = useState<ModalKind>(null);
  const [storage, setStorage] = useState<Storage>('DECIDE LATER');
  const [shipment, setShipment] = useState<Shipment>('DECIDE LATER');
  // N2-closeout — the cap that triggered the active cap-exceeded modal, so the
  // modal copy reflects the step's REAL `maxSelections` (e.g. a novel step
  // authored at 3) instead of the old hardcoded "6 items" / "1 item". Only
  // ever set for a FINITE cap (unlimited steps never trip the modal).
  const [capModalMax, setCapModalMax] = useState<number | undefined>(undefined);

  const qtyInputRef = useRef<HTMLInputElement | null>(null);

  const [cart, setCart] = useState<Cart>(() => ({
    box: {
      themeId: '',
      boxName: '',
      packagingId: '',
      packagingOther: '',
      merchIds: [],
      merchOther: '',
      finalTouchIds: [],
      finalTouchOther: '',
    },
    qty: defaults.defaultQuantity,
    itemQuantities: {},
    pricing: { unit: 0, total: 0, currency: defaults.currencySymbol ?? '€' },
  }));

  // Live flow refresh — when the content API delivers an updated doc for
  // the box the customer is currently configuring (or a lazily-loaded
  // flow lands in the registry), swap to its OWN data. Keyed on the
  // clicked category id, NOT a spring/office literal. Unknown ids leave
  // the flow untouched (the not-found sentinel set by handleCategorySelection
  // stays), never reverting to Spring.
  useEffect(() => {
    if (!selectedCategory) return;
    const next = flows[selectedCategory];
    if (!next) return;
    setFlow(next);
    // F2 — a LAZILY-resolved box (not in the static landing categories) opened
    // with the not-found sentinel, so `handleCategorySelection` could not apply
    // the single-select smart default; eager Spring/Office did. Apply it here
    // the moment the real flow lands, matching eager behaviour — but ONLY when
    // the resolved flow's first step has no selection yet, so a choice the user
    // already made (or a live re-delivery of an in-progress box) is never
    // clobbered. For eager flows this is a no-op (the default is already set).
    const firstStep = next.steps[0];
    if (!firstStep) return;
    setSelections((prev) => {
      if ((prev[firstStep.id]?.length ?? 0) > 0) return prev;
      const smart = smartDefaultSelections(next);
      return Object.keys(smart).length > 0 ? { ...prev, ...smart } : prev;
    });
  }, [flows, selectedCategory]);

  // Phase N2b — preview auto-open. When the bundle is opened at
  // `?preview=<flowId>` (the admin's "Preview on site" button), open that
  // draft box's configurator as soon as its doc lands in `flows` (the
  // ContentProvider's credentialed preview fetch). Guarded so it fires once —
  // the operator can still navigate freely afterwards.
  const previewOpened = useRef(false);
  useEffect(() => {
    if (!isPreview || !previewFlowId || previewOpened.current) return;
    const draft = flows[previewFlowId];
    if (!draft) return;
    previewOpened.current = true;
    setSelectedCategory(previewFlowId);
    setFlow(draft);
    setSelectedBox({
      // N5b — admin-driven name (category label → flow title → humanized id),
      // never the raw id. F22 — the suffix is added by `resolveBoxDisplayName`,
      // which never says BOX twice.
      name: resolveBoxDisplayName(previewFlowId, categories, flows),
      // N6c — curated → box's own hero → shared default (never a blank image).
      image: resolveBoxImage(categoryImageMap, previewFlowId, draft),
    });
    setSelections(smartDefaultSelections(draft));
    setOtherValues({});
    setView('configurator');
    setCurrentStepIndex(0);
  }, [isPreview, previewFlowId, flows, categories, categoryImageMap]);

  // Preload the curated category images so the first configurator paint is
  // instant. N6c — the hardcoded `easter_box.webp` (Spring's hero) preload was
  // removed: it was Spring-specific and redundant (Spring's own category image
  // is already in `categoryImageMap` and preloaded by the loop). Every box's
  // hero now comes through the admin-driven map, so nothing is id-templated.
  useEffect(() => {
    for (const id of Object.keys(categoryImageMap)) {
      const img = new Image();
      img.src = categoryImageMap[id];
    }
  }, [categoryImageMap]);

  const currentStep: Step = flow.steps[currentStepIndex];

  // Phase N2b — the legacy three "Other…" accessors are a positional view over
  // the generic `otherValues` store. Slot 0 = first option step, 1 = second,
  // 2 = third — i.e. packaging / merch / final-touch for Spring/Office. This
  // keeps the ConfiguratorView + CheckoutPage prop contracts unchanged while
  // the source of truth is fully structure-driven.
  const optionStepIds = useMemo(
    () => getOptionSteps(flow).map((s) => s.id),
    [flow],
  );
  const legacyOther = (slot: number): string =>
    optionStepIds[slot] ? otherValues[optionStepIds[slot]] ?? '' : '';
  const setLegacyOther = (slot: number) => (value: string) => {
    const id = optionStepIds[slot];
    if (id) setStepOther(id, value);
  };
  const packagingOther = legacyOther(0);
  const merchOther = legacyOther(1);
  const finalTouchOther = legacyOther(2);
  const setPackagingOther = useCallback(setLegacyOther(0), [optionStepIds, setStepOther]);
  const setMerchOther = useCallback(setLegacyOther(1), [optionStepIds, setStepOther]);
  const setFinalTouchOther = useCallback(setLegacyOther(2), [optionStepIds, setStepOther]);

  // Quantity prompt on summary
  useEffect(() => {
    if (currentStep.type === 'summary' && !hasSetQuantity) setModal('quantityPrompt');
  }, [currentStep.type, hasSetQuantity]);

  // Redesign pass B (item 14) — the 10-second auto-redirect after success is
  // gone. The success screen is a real post-conversion surface (reference,
  // recap, next step); the customer leaves it explicitly via "Back to home"
  // (`resetAfterSuccess`).

  // Scroll/focus on summary entry
  useEffect(() => {
    if (currentStep.type === 'summary') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const timer = setTimeout(() => qtyInputRef.current?.focus({ preventScroll: true }), 500);
      return () => clearTimeout(timer);
    }
  }, [currentStep.type]);

  // Sync cart with selections (Phase N2b — structure-driven).
  //
  // The per-step pricing loop is unchanged (it already iterated every step
  // generically). The denormalisation into `cart.box` now goes through
  // `deriveCartBox`, which projects the flow's OWN option steps positionally
  // onto the legacy packaging/merch/finalTouch slots. For Spring/Office
  // (packaging → merch → final-touch) this is byte-identical to the old
  // literal mapping; for a novel box the same positions are filled, so the
  // box is no longer empty at checkout (the N1 HIGH dead-end).
  useEffect(() => {
    if (!selectedBox) return;

    // Chapter 10 (P2b): the loop moved to `computeUnitBoxPrice` so the sticky
    // bar's quantity hint can ask "what would this box cost at 100?" with the
    // same arithmetic that produced the price on screen. Behaviour unchanged.
    const unitBoxPrice = computeUnitBoxPrice(flow, selections, cart.qty);

    const total = unitBoxPrice * cart.qty;
    const box = deriveCartBox(
      flow,
      selections,
      otherValues,
      selectedCategory || '',
      selectedBox.name,
    );

    setCart((prev) => ({
      ...prev,
      box,
      pricing: { unit: unitBoxPrice, total, currency: defaults.currencySymbol ?? '€' },
    }));
  }, [
    selectedBox,
    selections,
    flow,
    selectedCategory,
    otherValues,
    cart.qty,
    defaults.currencySymbol,
  ]);

  // Abandonment guard + ESC closes modals
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isSubmitted && currentStepIndex > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModal(null);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isSubmitted, currentStepIndex]);

  const updateQuantity = useCallback((newQty: number) => {
    setCart((prev) => ({ ...prev, qty: Math.max(1, newQty) }));
    // Redesign pass B (item 7) — any deliberate quantity change (the sticky
    // bar stepper is the single setting surface) counts as "quantity set",
    // so the summary's quantity prompt only fires for untouched quantities.
    setHasSetQuantity(true);
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setSelections((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((stepId) => {
        next[stepId] = next[stepId].filter((id) => id !== itemId);
      });
      return next;
    });
    setCart((prev) => {
      const next = { ...prev.itemQuantities };
      delete next[itemId];
      return { ...prev, itemQuantities: next };
    });
  }, []);

  const clearCart = useCallback(() => {
    setSelections({});
    setOtherValues({});
    setHasSetQuantity(false);
    setCart({
      box: { themeId: '', boxName: '', packagingId: '', merchIds: [], finalTouchIds: [] },
      qty: defaults.defaultQuantity,
      itemQuantities: {},
      pricing: { unit: 0, total: 0, currency: defaults.currencySymbol ?? '€' },
    });
    setStorage('DECIDE LATER');
    setShipment('DECIDE LATER');
    setLogos([]);
    setDeliveryDate('');
    setNotes('');
    setSelectedBox(null);

    if (pendingCategory) {
      const id = pendingCategory;
      setSelectedCategory(id);

      // Phase N1a — open the pending box's OWN flow (by id), lazy-loading
      // if needed, and apply its generic smart default. Previously this
      // branch left the flow as-is and leaned on the spring/office
      // live-refresh effect, which silently kept Spring for any other id.
      const registered = flows[id];
      const nextFlow = registered ?? NOT_FOUND_FLOW;
      setFlow(nextFlow);
      if (!registered && loadFlow) {
        void loadFlow(id).then((loaded) => {
          if (loaded) setFlow((prev) => (prev === NOT_FOUND_FLOW ? loaded : prev));
        });
      }
      // NOTE: selections intentionally stay empty here (set above) — the
      // clear-confirm box-switch has never applied the smart default
      // pre-selection (only the direct landing-tile path does). Preserved
      // verbatim so the visual baseline is unchanged.

      setSelectedBox({
        // N5b — admin-driven name (category label → flow title → humanized id).
        // F22 — one owner of the " BOX" suffix; never doubled.
        name: resolveBoxDisplayName(id, categories, flows),
        // N6c — curated → box's own hero → shared default (never a blank image).
        image: resolveBoxImage(categoryImageMap, id, nextFlow),
      });
      setView('configurator');
      setPendingCategory(null);
    } else {
      setView('landing');
    }
    setCurrentStepIndex(0);
    setModal(null);
    window.scrollTo(0, 0);
  }, [pendingCategory, defaults, categories, categoryImageMap, flows, loadFlow]);

  const checkDependencies = useCallback(
    (step: Step) => {
      if (!step.dependsOn) return true;
      return step.dependsOn.every((dep) => {
        const selected = selections[dep.stepId] || [];
        return dep.allowedValues.some((val) => selected.includes(val));
      });
    },
    [selections],
  );

  const nextStep = useCallback(() => {
    // Phase N1a/N2b — the "advance to checkout" branch fires on the LAST
    // option step (final-touch for Spring/Office), derived from the flow's own
    // option-step list rather than a literal id. The readiness gate is now
    // structure-driven (`isCheckoutReady`): the first two option steps must be
    // filled. For Spring/Office this is exactly the old packaging+merch gate;
    // for a novel box (vessel/treats/wrap) it opens once vessel+treats are
    // chosen — fixing the N1 HIGH dead-end.
    const optionSteps = getOptionSteps(flow);
    const isLastOptionStep =
      optionSteps.length > 0 && currentStep.id === optionSteps[optionSteps.length - 1].id;
    if (isLastOptionStep) {
      if (!isCheckoutReady(flow, cart.box)) {
        setModal('configError');
        return;
      }
      setView('checkout');
      window.scrollTo(0, 0);
      return;
    }
    let nextIndex = currentStepIndex + 1;
    while (nextIndex < flow.steps.length && !checkDependencies(flow.steps[nextIndex])) {
      nextIndex++;
    }
    if (nextIndex < flow.steps.length) {
      setCurrentStepIndex(nextIndex);
      window.scrollTo(0, 0);
    }
  }, [currentStep.id, currentStepIndex, flow, cart.box, checkDependencies]);

  const prevStep = useCallback(() => {
    if (currentStepIndex === 0) {
      setView('landing');
      window.scrollTo(0, 0);
      return;
    }
    let prevIndex = currentStepIndex - 1;
    while (prevIndex >= 0 && !checkDependencies(flow.steps[prevIndex])) prevIndex--;
    if (prevIndex >= 0) {
      setCurrentStepIndex(prevIndex);
      window.scrollTo(0, 0);
    }
  }, [currentStepIndex, flow.steps, checkDependencies]);

  const toggleOption = useCallback(
    (optionId: string) => {
      const currentSelections = selections[currentStep.id] || [];
      // "Other…" detection is owned by `isOtherOption` (first-class flag +
      // legacy id convention). Multi-select + the cap are driven by the
      // authored `maxSelections` (0 = unlimited; 1 = single; N = max-N).
      const option = currentStep.options?.find((o) => o.id === optionId);
      const isOther = isOtherOption(option ?? { id: optionId });
      const isUnlimited = isUnlimitedStep(currentStep);
      const max = getMaxSelections(currentStep);

      if (currentSelections.includes(optionId)) {
        setSelections({
          ...selections,
          [currentStep.id]: currentSelections.filter((id) => id !== optionId),
        });
        setCart((prev) => {
          const next = { ...prev.itemQuantities };
          delete next[optionId];
          return { ...prev, itemQuantities: next };
        });
        if (isOther) setStepOther(currentStep.id, '');
      } else {
        if (max === 1) {
          setSelections({ ...selections, [currentStep.id]: [optionId] });
        } else if (isUnlimited || currentSelections.length < max) {
          setSelections({
            ...selections,
            [currentStep.id]: [...currentSelections, optionId],
          });
        } else {
          // The cap is exceeded (only reachable for a FINITE cap — `max` here
          // is a real positive integer). Surface the real cap so the modal
          // copy reads correctly for ANY bounded step. The modal VARIANT is
          // chosen by the step itself: a step with category tabs (merch-like)
          // shows the package-icon variant, otherwise the plain variant —
          // not by a hardcoded id.
          setCapModalMax(max);
          setModal(stepHasCategoryTabs(currentStep) ? 'maxMerch' : 'maxFinalTouch');
        }
      }
    },
    [selections, currentStep, setStepOther],
  );

  const handleCategorySelection = useCallback(
    (id: string) => {
      const hasProgress =
        cart.box.packagingId ||
        cart.box.merchIds.length > 0 ||
        cart.box.finalTouchIds.length > 0;
      if (hasProgress) {
        setPendingCategory(id);
        setModal('clearConfirm');
        return;
      }
      setSelectedCategory(id);

      // Phase N1a — open the flow whose id matches the clicked tile. If it
      // isn't in the registry yet, fetch it on demand and hold the
      // not-found sentinel meanwhile (never Spring). The live-refresh
      // effect swaps in the real flow once `loadFlow` resolves into the
      // `flows` prop.
      const registered = flows[id];
      const nextFlow = registered ?? NOT_FOUND_FLOW;
      setFlow(nextFlow);
      if (!registered && loadFlow) {
        void loadFlow(id).then((loaded) => {
          // The flows-prop update from the provider drives the actual
          // swap; this .then is just to kick off the fetch. If the API
          // has no such flow, `loaded` is null and the sentinel stays.
          if (loaded) setFlow((prev) => (prev === NOT_FOUND_FLOW ? loaded : prev));
        });
      }

      // N5b — admin-driven name (category label → flow title → humanized id),
      // never the raw id. The chain reads the freshest data we have: the
      // category surface + the loaded flow registry. F22 — the display name
      // carries the word BOX exactly once.
      const boxDisplayName = resolveBoxDisplayName(id, categories, flows);
      // N6c — curated category image → box's own hero → shared default; never
      // the old id-templated `${id}_box.webp` guess (which 404s for novel boxes).
      const boxImage = resolveBoxImage(categoryImageMap, id, nextFlow);
      setSelectedBox({ name: boxDisplayName, image: boxImage });

      // Smart default — pre-select the first option of the first step when
      // that step is a single-select option step. For Spring/Office this
      // reproduces `{ packaging: ['box'] }` byte-identically (their first
      // step is `packaging`, maxSelections 1, first option `box`), but is
      // now derived from the flow's own data rather than an id literal.
      setSelections(smartDefaultSelections(nextFlow));
      setOtherValues({});
      setView('configurator');
      setCurrentStepIndex(0);
      window.scrollTo(0, 0);
    },
    [cart.box, flows, categories, categoryImageMap, loadFlow],
  );

  /**
   * The configurator's own `upload`-type step. F15 — it accepts a set, like the
   * checkout's picker, and REPLACES the selection rather than appending: this
   * input is the whole control for that step, so what the customer picks is
   * what is attached.
   *
   * Two caps apply. `checkLogoSelection` is the shared rule the upload endpoint
   * enforces (count, per-logo bytes, total bytes) — checking it here means a
   * bad pick is refused now instead of costing the customer every logo at
   * submit time. The step's authored `maxFileSizeMB` is honoured on top, and
   * only when it is STRICTER, so an operator can tighten but not loosen a cap
   * the server will refuse anyway.
   */
  const handleLogoUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      if (picked.length === 0) return;
      const check = checkLogoSelection(picked);
      if (!check.ok) {
        setError(check.message);
        return;
      }
      const authoredMb = currentStep.maxFileSizeMB;
      if (authoredMb && authoredMb * 1024 * 1024 < MAX_LOGO_BYTES) {
        const tooBig = picked.find((f) => f.size > authoredMb * 1024 * 1024);
        if (tooBig) {
          setError(`Bestand is te groot. Maximaal ${authoredMb}MB.`);
          return;
        }
      }
      Promise.all(
        picked.map(
          (file) =>
            new Promise<LogoSelection>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () =>
                resolve({ file, preview: reader.result as string });
              reader.readAsDataURL(file);
            }),
        ),
      ).then((selections) => {
        setLogos(selections);
        setError(null);
      });
    },
    [currentStep.maxFileSizeMB, setLogos],
  );

  const handlePricingImport = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        const rows = text.split('\n').map((row) => row.split(','));
        const newFlow = { ...flow };
        rows.slice(1).forEach((row) => {
          if (row.length < 7) return;
          const itemId = row[0].trim();
          const minBoxes = parseInt(row[1]) || 25;
          const p25 = parseFloat(row[2]) || 0;
          const p50 = parseFloat(row[3]) || 0;
          const p100 = parseFloat(row[4]) || 0;
          const p150 = parseFloat(row[5]) || 0;
          const p250 = parseFloat(row[6]) || 0;
          newFlow.steps.forEach((step) => {
            step.options?.forEach((opt) => {
              if (opt.id === itemId) {
                opt.minBoxes = minBoxes;
                opt.tieredPrices = {
                  '25-49': p25,
                  '50-99': p50,
                  '100-149': p100,
                  '150-249': p150,
                  '250+': p250,
                };
              }
            });
          });
        });
        setFlow(newFlow);
      };
      reader.readAsText(file);
    },
    [flow],
  );

  const handleSubmit = useCallback(
    async (agreedToNewsletter: boolean) => {
      if (isPreview) {
        // Preview iframe — no real submission.
        setError(
          'Submission disabled in preview. This is what editors see; the live site will post for real.',
        );
        return;
      }
      setIsSubmitting(true);
      setError(null);

      // M4 (customer-logo pipeline) — upload the logo BYTES first, then thread
      // the returned pointers into `design`. Logos are OPTIONAL and MUST NOT
      // block the order: on the FIRST failure we surface a friendly, recoverable
      // message and STOP (no order POST) so the customer can fix the file +
      // retry. If they submit AGAIN (flag still set) we proceed WITHOUT the
      // bytes — the filenames + sizes still flow. Changing the selection clears
      // the flag (see `setLogos`).
      //
      // F15 — every logo goes up in ONE request and the endpoint accepts or
      // refuses the set as a whole, so `logoBlobs` is either as long as the
      // selection or empty. There is no "two of three attached" state, and the
      // copy therefore never has to claim one.
      let logoBlobs: LogoBlobPointer[] = [];
      if (logos.length > 0 && !logoUploadFailed) {
        try {
          logoBlobs = await uploadLogos(logos.map((l) => l.file));
        } catch (err) {
          setLogoUploadFailed(true);
          setError(
            err instanceof LogoUploadError
              ? err.message
              : logos.length > 1
                ? "We couldn't attach your logos right now. You can retry, or submit without them and email them later."
                : "We couldn't attach your logo right now. You can retry, or submit without it and email it later.",
          );
          setIsSubmitting(false);
          return;
        }
      }

      // Ch6 — resolve the positional slot labels via `buildPositionalSlots`,
      // the single owner of "which option step owns slot 0/1/2 and what are its
      // selected items' labels" (shared with CheckoutPage + ShoppingCart),
      // instead of re-walking the option steps + indexing slots here. For
      // Spring/Office this is packaging / merch / final-touch — byte-identical
      // labels. For a novel box (vessel/treats/wrap) the labels resolve too.
      // These feed the persisted/emailed positional contract (ADR 0001) — the
      // resolution is identical (authored label, else the raw id).
      const slots = buildPositionalSlots(flow, cart.box);
      const labelInSlot = (slot: number, id: string): string =>
        slots[slot].items.find((i) => i.id === id)?.label || id;

      const packagingLabel = labelInSlot(0, cart.box.packagingId);
      const merchLabels = cart.box.merchIds.map((id) => ({ id, label: labelInSlot(1, id) }));
      const finalTouchLabels = cart.box.finalTouchIds.map((id) => ({
        id,
        label: labelInSlot(2, id),
      }));

      // The generic, self-describing per-step array (every option step). Carries
      // the typed "Other…" value as `customText`. Stored alongside the legacy
      // projection so the admin /submissions UI can render ANY box structure.
      const submissionSteps = buildSubmissionSteps(flow, selections, otherValues);

      const ctx = buildEmailContext({
        customerInfo,
        cart,
        packagingLabel,
        packagingOther: cart.box.packagingOther || '',
        merchLabels: merchLabels.map((m) => m.label),
        merchOther: cart.box.merchOther || '',
        finalTouchLabels: finalTouchLabels.map((m) => m.label),
        finalTouchOther: cart.box.finalTouchOther || '',
        deliveryDate,
        storage,
        shipment,
        notes,
      });

      const template = emailTemplate?.bodyHtmlTemplate
        ? emailTemplate
        : LEGACY_EMAIL_TEMPLATE;
      const { body: emailBody } = renderEmail(template, ctx);

      // Phase N2b — resolve the submission flowId robustly. A box authored in
      // admin is a `FlowDoc` keyed on `id` (no `flowId` field); the public
      // bundle's bundled fallbacks use `flowId`. Coalesce so EVERY box yields
      // a non-empty flowId (the backend's `NewSubmissionInputSchema.flowId` is
      // `.min(1)` — an empty value would 400 the submission).
      const resolvedFlowId =
        flow.flowId || (flow as { id?: string }).id || selectedCategory || '';

      const submission = buildSubmission({
        flowId: resolvedFlowId,
        cart,
        customerInfo,
        packagingLabel,
        merchLabels,
        finalTouchLabels,
        deliveryDate,
        storage,
        shipment,
        notes,
        logos: logos.map((l) => l.file),
        // M4 — pointers only when the bytes were actually stored; empty on
        // no-logo OR proceed-without-logo so the body stays byte-identical.
        logoBlobs,
        agreedToNewsletter,
        emailBody,
        formattedPrice: ctx.pricing.formattedTotal,
        steps: submissionSteps,
      });

      try {
        // M+5 Option B: POST to admin's `/api/send` via VITE_ADMIN_BASE_URL
        // (cross-origin in staging/prod, same-origin via the Vite proxy
        // locally). The orphan public-bundle Function was removed in M+5
        // because the M+3.5 submission pipeline (Cosmos + Graph + audit)
        // lives in the admin. See `lib/sendApi.ts` for the URL contract.
        const response = await fetch(resolveSendUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submission),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.details || errorData.error || 'Verzenden mislukt');
        }
        // Item 14 — surface a reference number on the success screen. A
        // pipeline-returned id wins; otherwise generate one client-side.
        let data: Record<string, unknown> = {};
        try {
          data = (await response.json()) as Record<string, unknown>;
        } catch {
          // No/invalid JSON body — fall through to the client reference.
        }
        const serverRef = [data.reference, data.submissionId, data.id].find(
          (v): v is string => typeof v === 'string' && v.length > 0,
        );
        setSubmissionRef(serverRef ?? generateClientReference());
        setIsSubmitted(true);
        window.scrollTo(0, 0);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Er is iets misgegaan bij het verzenden. Probeer het later opnieuw.',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      flow,
      selectedCategory,
      cart,
      selections,
      otherValues,
      customerInfo,
      deliveryDate,
      storage,
      shipment,
      notes,
      logos,
      logoUploadFailed,
      emailTemplate,
      isPreview,
    ],
  );

  const goToStep = useCallback(
    (stepId: string) => {
      const index = flow.steps.findIndex((s) => s.id === stepId);
      if (index !== -1) {
        setCurrentStepIndex(index);
        window.scrollTo(0, 0);
      }
    },
    [flow.steps],
  );

  const goToConfiguratorStep = useCallback(
    (stepId: string) => {
      const index = flow.steps.findIndex((s) => s.id === stepId);
      if (index === -1) return;
      setView('configurator');
      setCurrentStepIndex(index);
      setModal(null);
      window.scrollTo(0, 0);
    },
    [flow.steps],
  );

  const isStepValid = useCallback(() => {
    const optionSteps = getOptionSteps(flow);
    const optionStepIndex = optionSteps.findIndex((s) => s.id === currentStep.id);
    const isLastOptionStep =
      optionSteps.length > 0 && optionStepIndex === optionSteps.length - 1;

    // The last option step (final-touch for Spring/Office) is optional —
    // always valid so the customer can proceed without picking a finishing
    // touch. Keyed on position, not the literal id.
    if (isLastOptionStep) return true;
    if (currentStep.type === 'form') {
      return !!currentStep.fields?.every((f) => !f.required || customerInfo[f.name]);
    }
    if (currentStep.type === 'upload') return true;
    if (currentStep.type === 'summary') return true;

    const selected = selections[currentStep.id] || [];
    // Phase N2b — the free-text "Other…" value is keyed generically by the
    // current step's id (any number of Other slots on any step).
    const hasOther = !!otherValues[currentStep.id];

    return selected.length > 0 || hasOther;
  }, [flow, currentStep, selections, customerInfo, otherValues]);

  const resetAfterSuccess = useCallback(() => {
    setIsSubmitted(false);
    setSubmissionRef(null);
    setView('landing');
    setCurrentStepIndex(0);
    setSelections({});
    setCustomerInfo({});
    setLogos([]);
    setSelectedBox(null);
    setSelectedCategory(null);
    window.scrollTo(0, 0);
  }, []);

  return {
    // state
    flow,
    view,
    setView,
    selectedCategory,
    selectedBox,
    currentStep,
    currentStepIndex,
    setCurrentStepIndex,
    selections,
    customerInfo,
    setCustomerInfo,
    logos,
    setLogos,
    deliveryDate,
    setDeliveryDate,
    notes,
    setNotes,
    isSubmitting,
    isSubmitted,
    submissionRef,
    // M4 — true after a recoverable logo-upload failure: the next submit
    // proceeds without the logo. CheckoutPage uses this to nudge the CTA copy.
    logoUploadFailed,
    packagingOther,
    setPackagingOther,
    merchOther,
    setMerchOther,
    finalTouchOther,
    setFinalTouchOther,
    // Phase N2b — generic per-step "Other…" free-text accessors. The
    // ConfiguratorView uses these to render an Other input on ANY step that
    // has an `isOther` option, not just the first three positions.
    otherValues,
    setStepOther,
    abandonEmail,
    setAbandonEmail,
    error,
    cart,
    storage,
    setStorage,
    shipment,
    setShipment,
    isCartOpen,
    setIsCartOpen,
    modal,
    setModal,
    capModalMax,
    qtyInputRef,
    hasSetQuantity,
    setHasSetQuantity,
    // actions
    updateQuantity,
    removeItem,
    clearCart,
    checkDependencies,
    nextStep,
    prevStep,
    toggleOption,
    handleCategorySelection,
    handleLogoUpload,
    handlePricingImport,
    handleSubmit,
    goToStep,
    goToConfiguratorStep,
    isStepValid,
    resetAfterSuccess,
  };
}
